<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contract',
    path: '/contracts/:id',
    component: 'Contract',
    params: [
      {
        name: 'id',
        prop: 'contractId',
      },
      {
        name: 'rev',
        prop: 'revision_number',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Contract',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contractId: {
      type: String,
      required: false,
    },
    revision_number: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const tabManager = ref(null);
  const item = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_8564662037462133_parentTo = computed(() => ({
    name: 'app.contracts',
  }));

  const $data = computed(() => ({
    tabManager: tabManager.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_parentTo: b_8564662037462133_parentTo.value,
  }));

  const $$load_tabManager = async () => {
    return new $app.value.utils.LazyTabManager();
  };
  const $$load_item = async () => {
    return $fn.value.fetch('read_contract', {
      asResource: true,
      parameters: { id: props.contractId, rev: props.revision_number },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'tabManager' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_parentTo', nodeId: '8564662037462133' },
    ],
    loaders: { tabManager: $$load_tabManager, item: $$load_item },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      tabManager,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const readonly = computed(() => {
    if (loaded.value) {
      return item.value.locked;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.snapshot('contract_number'),
          $fn.value.gettext('Contract'),
        ],
        icon: $config.value.contract.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contractId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contractId');
    }
  );
  watch(
    () => props.revision_number,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('revision_number');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_05jqwmu5t_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_05jqwmu5t =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_05jqwmu5t_getActionArgs,
    });
  const inst_evh_7315092382398562_close_zdiykxyzu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_05jqwmu5t,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_zdiykxyzu],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_h8x3jfv3v =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_w6qte6s0z =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_h8x3jfv3v,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_w6qte6s0z],
    event: 'reload',
  });
  const act_4615685607825208_reloadComponentData_5c0ghpnjl =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_9odst948m =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_5c0ghpnjl,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_9odst948m],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_closeComponent_wcwzo09a0 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2095089031469157_success_mzbq2s3t0 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_closeComponent_wcwzo09a0,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_2095089031469157_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_success_mzbq2s3t0],
    event: 'success',
  });
  const act_2679161326024595_reloadComponentData_7yy97743t =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2679161326024595_reload_3h88q2aqs =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_reloadComponentData_7yy97743t,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2679161326024595_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_reload_3h88q2aqs],
    event: 'reload',
  });
  const act_7987652045662005_reloadComponentData_82n6ewif4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7987652045662005_reload_upq1v2wga =
    new actionTypes.EventHandlerImpl({
      action: act_7987652045662005_reloadComponentData_82n6ewif4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7987652045662005_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7987652045662005_reload_upq1v2wga],
    event: 'reload',
  });
  const act_6490121676898844_reloadComponentData_eq1zv5e4o =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6490121676898844_reload_hdr9sxl31 =
    new actionTypes.EventHandlerImpl({
      action: act_6490121676898844_reloadComponentData_eq1zv5e4o,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6490121676898844_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6490121676898844_reload_hdr9sxl31],
    event: 'reload',
  });
  const act_2789477278097085_reloadComponentData_q40dosldo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2789477278097085_reload_xmqt6d6di =
    new actionTypes.EventHandlerImpl({
      action: act_2789477278097085_reloadComponentData_q40dosldo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2789477278097085_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2789477278097085_reload_xmqt6d6di],
    event: 'reload',
  });
  const act_6182083303232630_reloadComponentData_tc9772c0b =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6182083303232630_reload_ciqtvnlwu =
    new actionTypes.EventHandlerImpl({
      action: act_6182083303232630_reloadComponentData_tc9772c0b,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6182083303232630_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6182083303232630_reload_ciqtvnlwu],
    event: 'reload',
  });
  const act_5361955607147334_reloadComponentData_hhwa1rndb =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5361955607147334_reload_4vwu9pd7q =
    new actionTypes.EventHandlerImpl({
      action: act_5361955607147334_reloadComponentData_hhwa1rndb,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5361955607147334_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5361955607147334_reload_4vwu9pd7q],
    event: 'reload',
  });
  const act_384677887161204_reloadComponentData_lcm83ywh9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_384677887161204_reload_x2ou7guck =
    new actionTypes.EventHandlerImpl({
      action: act_384677887161204_reloadComponentData_lcm83ywh9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_384677887161204_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_384677887161204_reload_x2ou7guck],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_zdiykxyzu,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_w6qte6s0z,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_clickPreventStop_9odst948m,
    reload: inst_evh_4615685607825208_clickPreventStop_9odst948m,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_success_mzbq2s3t0,
    inst_evh_2095089031469157_success,
    inst_evh_2679161326024595_reload_3h88q2aqs,
    inst_evh_2679161326024595_reload,
    inst_evh_7987652045662005_reload_upq1v2wga,
    inst_evh_7987652045662005_reload,
    inst_evh_6490121676898844_reload_hdr9sxl31,
    inst_evh_6490121676898844_reload,
    inst_evh_2789477278097085_reload_xmqt6d6di,
    inst_evh_2789477278097085_reload,
    inst_evh_6182083303232630_reload_ciqtvnlwu,
    inst_evh_6182083303232630_reload,
    inst_evh_5361955607147334_reload_4vwu9pd7q,
    inst_evh_5361955607147334_reload,
    inst_evh_384677887161204_reload_x2ou7guck,
    inst_evh_384677887161204_reload,
  };

  const evh_7315092382398562_close_zdiykxyzu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_zdiykxyzu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_w6qte6s0z = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_w6qte6s0z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_9odst948m = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_9odst948m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_mzbq2s3t0 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_mzbq2s3t0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload_3h88q2aqs = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload_3h88q2aqs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload_upq1v2wga = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload_upq1v2wga.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload_hdr9sxl31 = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload_hdr9sxl31.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload_xmqt6d6di = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload_xmqt6d6di.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6182083303232630_reload_ciqtvnlwu = async (event, scope) => {
    return await actions.inst_evh_6182083303232630_reload_ciqtvnlwu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6182083303232630_reload = async (event, scope) => {
    return await actions.inst_evh_6182083303232630_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5361955607147334_reload_4vwu9pd7q = async (event, scope) => {
    return await actions.inst_evh_5361955607147334_reload_4vwu9pd7q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5361955607147334_reload = async (event, scope) => {
    return await actions.inst_evh_5361955607147334_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_384677887161204_reload_x2ou7guck = async (event, scope) => {
    return await actions.inst_evh_384677887161204_reload_x2ou7guck.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_384677887161204_reload = async (event, scope) => {
    return await actions.inst_evh_384677887161204_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Contract"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page--v2 d-flex flex-column" v-if="loaded">
      <MaterialTabs
        :tabManager="tabManager"
        :title="item.snapshot('contract_number')"
        :parentText="gettext('Contracts')"
        :parentTo="b_8564662037462133_parentTo"
        class="h-100"
      >
        <template #actions>
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            size="sm"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BtnDeleteResource
            :resource="item"
            :dialogHeader="pgettext('Contract/DialogTitle', 'Delete contract')"
            @success="evh_2095089031469157_success($event, {})"
          >
          </BtnDeleteResource>
          <UcContractRevisionDropdown :contract="item">
          </UcContractRevisionDropdown>
        </template>
        <BTab :title="gettext('General')" class="container float-start">
          <ItpRow v-if="tabManager.visited(0)">
            <ItpCol :w="6">
              <UcContractGeneralInfo
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_2679161326024595_reload($event, {})"
              >
              </UcContractGeneralInfo>
              <UcContractServices
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_7987652045662005_reload($event, {})"
              >
              </UcContractServices>
              <UcContractEvents
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_6490121676898844_reload($event, {})"
              >
              </UcContractEvents>
            </ItpCol>
            <ItpCol :w="6">
              <UcContractLifecycle
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_2789477278097085_reload($event, {})"
              >
              </UcContractLifecycle>
              <UcContractInvoicing
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_6182083303232630_reload($event, {})"
              >
              </UcContractInvoicing>
              <UcContractTerms
                :contract="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_5361955607147334_reload($event, {})"
              >
              </UcContractTerms>
              <UcCommentsList
                :parentId="item.revision_id"
                :parent="item"
                :reload="lastLoaded"
                class="pb-5"
              >
              </UcCommentsList>
              <UcAttachmentsList
                :parent_id="item.revision_id"
                :parent="item"
                :reload="lastLoaded"
                :readonly="readonly"
                class="pb-5"
                @reload="evh_384677887161204_reload($event, {})"
              >
              </UcAttachmentsList>
            </ItpCol>
          </ItpRow>
        </BTab>
        <BTab
          :title="pgettext('Contract', 'Billing list')"
          class="container-fluid h-100"
        >
          <UcBillingList
            :contractId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(1)"
          >
          </UcBillingList>
        </BTab>
        <BTab
          :title="pgettext('Contract', 'Invoices')"
          class="container-fluid h-100"
        >
          <InvoiceLinesView
            :contractId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(2)"
          >
          </InvoiceLinesView>
        </BTab>
      </MaterialTabs>
    </div>
  </ItpPage>
</template>
